'use client';

import { useAuth } from 'AuthBff/use-auth';
import { PersonRoundedIcon, SignInButton, SignUpButton, Skeleton, type TooltipPlacement, UserAvatarMenu, type UserAvatarMenuItem } from '@bomdisoft/components';
import type { ReactNode } from 'react';

export type AuthControlsProps = {
  signInLabel: string;
  signUpLabel: string;
  signOutLabel: string;
  manageAccountLabel: string;
  signInHref?: string;
  signUpHref?: string;
  /** Restyles the signed-out sign-up CTA for a product that does not take the
   * shared brand primary; prodantix's landing paints it its signal green. */
  signUpClassName?: string;
  accountHref?: string;
  logoutHref?: string;
  menuLabel?: string;
  /** Extra signed-in destinations (billing, settings…), appended after the
   * account link. They live here rather than in a product's public nav because
   * they only exist for a signed-in identity. */
  items?: UserAvatarMenuItem[];
  /** Destinations that lead the menu, before the account link: the product's
   * own app/console, so the primary destination is the first row. */
  leadingItems?: UserAvatarMenuItem[];
  /** The account-switcher rows for multi-account products, handed through to
   * `UserAvatarMenu` so they lead the shared account menu. */
  accountsSection?: ReactNode;
  /** Shows the signed-in name beside the avatar; see UserAvatarMenu.showName. */
  showUserName?: boolean;
  /** Side for the collapsed trigger's tooltip; see UserAvatarMenu.tooltipPlacement. */
  tooltipPlacement?: TooltipPlacement;
  /**
   * What to render during the auth boot. A public header (every landing) keeps
   * the default `'signed-out'`: the CTAs are the correct default and the boot's
   * refresh only ever UPGRADES to the account menu, so the header is useful from
   * the first paint and never waits on the network. An authenticated-by-construction
   * surface (the in-app console, behind `Protected`) passes `'skeleton'` to hold
   * an avatar-shaped placeholder, since flashing "Sign in" at a signed-in user
   * would be wrong.
   */
  loadingView?: 'signed-out' | 'skeleton';
};

export const AuthControls = ({
  accountHref,
  accountsSection,
  items = [],
  leadingItems = [],
  loadingView = 'signed-out',
  logoutHref = '/api/auth/logout',
  manageAccountLabel,
  menuLabel,
  showUserName,
  signInHref = '/api/auth/login',
  signInLabel,
  signOutLabel,
  signUpClassName,
  signUpHref = '/api/auth/login?prompt=create',
  signUpLabel,
  tooltipPlacement,
}: AuthControlsProps) => {
  const { state } = useAuth();

  const signedOut = (
    <>
      <SignInButton annotation='Sign in' href={signInHref} label={signInLabel} />
      <SignUpButton annotation='Create an account' className={signUpClassName} href={signUpHref} label={signUpLabel} />
    </>
  );

  if (state.status === 'loading') return loadingView === 'skeleton' ? <Skeleton height={28} variant='circular' width={28} /> : signedOut;

  if (state.status === 'unauthenticated') return signedOut;

  // Every leading row is an app or console the landing sends people OUT to, so
  // it opens in its own tab and carries the open-in-new glyph, which `external`
  // supplies together (user rule, platform-wide). Forced here rather than asked
  // of each landing: five of them had it missing for the same reason.
  const menuItems = [...leadingItems.map(item => ({ ...item, external: true })), ...(accountHref ? [{ annotation: 'Manage account', external: true, href: accountHref, icon: <PersonRoundedIcon />, label: manageAccountLabel, testId: 'auth-controls-account' }] : []), ...items];

  return (
    <UserAvatarMenu
      accountsSection={accountsSection}
      avatarUrl={state.user.picture ?? undefined}
      email={state.user.email}
      items={menuItems}
      menuLabel={menuLabel}
      name={state.user.name}
      onSignOut={() => {
        window.location.href = logoutHref;
      }}
      showName={showUserName}
      signOutAnnotation='Sign out'
      signOutLabel={signOutLabel}
      tooltipPlacement={tooltipPlacement}
    />
  );
};
